import type { ComponentType, CSSProperties } from 'react';
import type { Alignment, Cell, ColumnData, Font, MergedCell } from 'api';
import type { DataEditorProps, ValueViewerProps } from 'common/types/scalarSpreadsheet';
import { GridSelect } from 'components/FeaturedSpreadsheet/components';
import MultiSelectValueViewer from 'components/MultiSelectValueViewer';
import {
  AlphaReferenceType,
  ColumnReferenceType,
  ORDER_OF_MAGNITUDES,
  referenceColumnLegend,
  RowReferenceType,
  SpreadsheetNoteCellData,
  SpreadsheetNoteConfigCell,
  SpreadsheetNoteConfigCellMap,
  SpreadsheetNoteData,
  SpreadsheetNoteTableData,
} from 'components/SpreadsheetNotes/types';
import ExcelFormattedValueViewer from 'components/SpreadsheetNotes/utilities/ExcelFormattedValueViewer/ExcelFormattedValueViewer';
import getFillStyle from 'components/SpreadsheetNotes/utilities/GetFillStyle/getFillStyle';
import { parseExcelColorToCss } from 'components/SpreadsheetNotes/utilities/parseExcelColorToCss';
import {
  getXlsxColumnWidthPx,
  getXlsxRowHeightStyle,
  INTERNAL_REFERENCE_XLSX_ROW_HEIGHT_STYLE,
  ROW_REFERENCE_XLSX_ROW_HEIGHT_STYLE,
} from 'components/SpreadsheetNotes/utilities/xlsxLayout.utils';
import { ParserParams } from 'models/interfaces/spreadsheet-types/core-types';
import { alphabetGenerator } from 'utilities';
import { spreadsheetNoteReferenceListStyles, spreadsheetNoteYearListStyles } from './SpreadsheetNoteList.styles';
import { AttributeMappingType } from '../../../pages/Financials/financials-widget-drawer/types';
import SheetAttributeManager from '../../../services/sheet-attribute-manager/SheetAttributeManager';
import SelectValueViewer from '../../SelectValueViewer';
import type { OptionItem } from '../../SelectValueViewer/utilities/utilities.props';

type SpreadsheetCellBorderSide = {
  style?: string | null;
  color?: string | null;
};

type SpreadsheetCellBorder = {
  top?: SpreadsheetCellBorderSide | null;
  right?: SpreadsheetCellBorderSide | null;
  bottom?: SpreadsheetCellBorderSide | null;
  left?: SpreadsheetCellBorderSide | null;
};

type SpreadsheetCellWithStyle = Cell & {
  border?: SpreadsheetCellBorder | null;
};

type MultiSelectSpreadsheetValueViewerProps = ValueViewerProps & {
  value: string[];
};

type SpreadsheetGridSelectProps = DataEditorProps & {
  alignMenuToCellTop?: boolean;
  extraStyles?: unknown;
  isMulti?: boolean;
  matchFrom?: string;
  menuBoundaryMaxWidth?: number;
  menuBoundaryPadding?: number;
  menuBoundarySelector?: string;
  menuPosition?: 'absolute' | 'fixed';
  options: OptionItem[];
  usePortal?: boolean;
};

const SpreadsheetGridSelect = GridSelect as unknown as ComponentType<SpreadsheetGridSelectProps>;

const XLSX_PREVIEW_MENU_BOUNDARY_SELECTOR = '[data-content-variant="legacy-data-grid"]';
const XLSX_PREVIEW_MENU_BOUNDARY_MAX_WIDTH = 420;

const EXCEL_BORDER_STYLE_MAP: Record<string, string> = {
  dashDot: '1px dashed',
  dashDotDot: '1px dashed',
  dashed: '1px dashed',
  dotted: '1px dotted',
  double: '3px double',
  hair: '1px solid',
  medium: '2px solid',
  mediumDashDot: '2px dashed',
  mediumDashDotDot: '2px dashed',
  mediumDashed: '2px dashed',
  slantDashDot: '1px dashed',
  thick: '3px solid',
  thin: '1px solid',
};

const INTERNAL_REFERENCE_CELL_CLASS_NAME = 'xlsx-internal-reference-cell';
const ROW_REFERENCE_CELL_CLASS_NAME = 'xlsx-row-reference-cell';

const getCellClassName = (...classNames: Array<string | false | null | undefined>) =>
  classNames.filter(Boolean).join(' ') || undefined;

const getBorderSideStyle = (side?: SpreadsheetCellBorderSide | null): string | undefined => {
  if (!side?.style) {
    return undefined;
  }

  const lineStyle = EXCEL_BORDER_STYLE_MAP[side.style] ?? '1px solid';
  const color = parseExcelColorToCss(side.color) ?? '#D0D7DE';

  return `${lineStyle} ${color}`;
};

const getBorderStyle = (border?: SpreadsheetCellBorder | null): CSSProperties => {
  const top = getBorderSideStyle(border?.top);
  const right = getBorderSideStyle(border?.right);
  const bottom = getBorderSideStyle(border?.bottom);
  const left = getBorderSideStyle(border?.left);

  return {
    ...(top ? { borderTop: top } : {}),
    ...(right ? { borderRight: right } : {}),
    ...(bottom ? { borderBottom: bottom } : {}),
    ...(left ? { borderLeft: left } : {}),
  };
};

const getRowDataByIndex = (
  rowData: Record<string | number, { row_height?: number | null }> | undefined,
  rowIndex: number,
  rowNumber: number
) => {
  if (!rowData) {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(rowData, rowIndex)) {
    return rowData[rowIndex];
  }

  return rowData[rowNumber];
};

const getVerticalAlignmentStyle = (alignment?: Alignment | null): CSSProperties => {
  switch (alignment?.vertical?.toLowerCase()) {
    case 'top':
      return { verticalAlign: 'top' };
    case 'center':
    case 'middle':
      return { verticalAlign: 'middle' };
    case 'bottom':
      return { verticalAlign: 'bottom' };
    default:
      return {};
  }
};

function getWidthStyle(isWidthValid: boolean, columnWidth: number | null | undefined, isPreview: boolean) {
  let widthStyle: CSSProperties = {};
  if (isWidthValid) {
    if (columnWidth === 0) {
      widthStyle = { display: 'none' };
    } else {
      const width = getXlsxColumnWidthPx(columnWidth);
      widthStyle = {
        ...(width ? { width, minWidth: width, maxWidth: width } : {}),
        ...(!isPreview ? { overflow: 'hidden' } : {}),
      };
    }
  }
  return widthStyle;
}

const spreadsheetNoteParser = (
  params: ParserParams<ColumnData, SpreadsheetNoteData, SpreadsheetNoteTableData, SpreadsheetNoteCellData>,
  isPreview: boolean,
  mapper?: SheetAttributeManager,
  zoomLevel?: number
) => {
  const { columns, rowConfig, initialObject, tableData } = params;
  const alphabet = alphabetGenerator([], columns.length);
  const mergedCells =
    initialObject?.content?.merged_cells?.reduce<Record<string, MergedCell>>((map, info) => {
      // eslint-disable-next-line no-param-reassign
      map[info.top_left] = info;
      return map;
    }, {}) || ({} as Record<string, MergedCell>);

  const attributeReferenceConfig = tableData?.attributeReferenceConfig;
  const isReferenceSheet = Boolean(tableData?.isAttributeMapperEnabled && attributeReferenceConfig);
  let iterableRowConfig = [...rowConfig];
  let iterableColumns = [...columns];
  let cells: SpreadsheetNoteConfigCellMap = {};
  const isMultiCell = attributeReferenceConfig?.selectionMode !== AttributeMappingType.SINGLE_CELL;
  const getSelectedRowsForReferenceRow = (rowNumber: number) =>
    mapper?.sheetAttributeMapping.sheetIndicatorMapping
      .filter(
        item =>
          item.gridIndicators.rows[rowNumber] &&
          item.sheet === tableData?.sheetName &&
          item.documentReferenceId === tableData?.documentId
      )
      .map(item => item.gridIndicators.rows[rowNumber].map(item => item.value))
      .flat() ?? [];

  if (isReferenceSheet && isMultiCell) {
    iterableRowConfig = rowConfig.slice(1);
    iterableColumns = columns.slice(1);

    const widthStyle = {
      minWidth: '175px',
    };

    const indicatorForSheet = mapper?.sheetAttributeMapping.sheetIndicatorMapping.find(
      item => item.sheet === initialObject?.name && item.documentReferenceId === tableData?.documentId
    );

    cells = {
      [`${referenceColumnLegend}0`]: {
        key: `${referenceColumnLegend}0`,
        rowNumber: 0,
        columnLegend: referenceColumnLegend,
        valueViewer: (props: ValueViewerProps) => <SelectValueViewer options={ORDER_OF_MAGNITUDES} {...props} />,
        dataEditor: (props: DataEditorProps) => (
          <SpreadsheetGridSelect
            {...props}
            alignMenuToCellTop
            extraStyles={spreadsheetNoteYearListStyles(zoomLevel ?? 1)}
            menuBoundaryMaxWidth={XLSX_PREVIEW_MENU_BOUNDARY_MAX_WIDTH}
            menuBoundarySelector={XLSX_PREVIEW_MENU_BOUNDARY_SELECTOR}
            menuPosition="fixed"
            options={ORDER_OF_MAGNITUDES}
            usePortal
          />
        ),
        readOnly: false,
        value: indicatorForSheet?.orderOfMagnitude ?? ORDER_OF_MAGNITUDES[0].value,
        style: {
          ...widthStyle,
          ...INTERNAL_REFERENCE_XLSX_ROW_HEIGHT_STYLE,
        },
        className: getCellClassName(INTERNAL_REFERENCE_CELL_CLASS_NAME, indicatorForSheet && 'cell-with-references'),
        data: {
          referenceType: AlphaReferenceType,
          sheetName: initialObject?.name || '',
        },
      },
    };
  }

  const columnCells = iterableColumns.reduce<SpreadsheetNoteConfigCellMap>((acc, column, columnIndex) => {
    const colLegend = alphabet[columnIndex];
    const columnWidth = initialObject?.content?.column_data?.[columnIndex]?.column_width;
    const isWidthValid = typeof columnWidth === 'number';

    let widthStyle = {};
    widthStyle = getWidthStyle(isWidthValid, columnWidth, isPreview);
    if (isReferenceSheet) {
      const selectedColumn = mapper?.sheetAttributeMapping.sheetIndicatorMapping
        .filter(
          item =>
            item.gridIndicators.columns[colLegend] &&
            item.sheet === tableData?.sheetName &&
            item.documentReferenceId === tableData?.documentId
        )
        .map(item => item.gridIndicators.columns[colLegend].map(item => item.value))
        .flat();

      const rowNumber = 0;
      const referenceColumnLegend = `${colLegend}`;
      const key = `${referenceColumnLegend}${rowNumber}`;
      const baseOptions = attributeReferenceConfig?.columns || [];

      // Enrich options with ordinate metadata for AI suggestion chips
      // Pass the column key to get metadata specific to this column
      const options = baseOptions.map(option => {
        const ordinateMetadata = mapper?.getOrdinateMetadata(
          option.value as string,
          colLegend,
          'column',
          tableData?.sheetName,
          tableData?.documentId
        );
        return {
          ...option,
          ordinate: ordinateMetadata,
        };
      });

      const cell: SpreadsheetNoteConfigCell = {
        key,
        rowNumber,
        columnLegend: referenceColumnLegend,
        valueViewer: (props: ValueViewerProps) =>
          isMultiCell ? (
            <MultiSelectValueViewer
              {...(props as MultiSelectSpreadsheetValueViewerProps)}
              options={options}
              useLabel={false}
              useAliasList={false}
            />
          ) : null,
        dataEditor: (props: DataEditorProps) =>
          isMultiCell ? (
            <SpreadsheetGridSelect
              {...props}
              alignMenuToCellTop
              extraStyles={spreadsheetNoteYearListStyles(zoomLevel ?? 1)}
              isMulti
              matchFrom="any"
              menuBoundaryMaxWidth={XLSX_PREVIEW_MENU_BOUNDARY_MAX_WIDTH}
              menuBoundarySelector={XLSX_PREVIEW_MENU_BOUNDARY_SELECTOR}
              menuPosition="fixed"
              options={options}
              usePortal
            />
          ) : null,
        readOnly: false,
        value: selectedColumn,
        style: {
          ...widthStyle,
          ...INTERNAL_REFERENCE_XLSX_ROW_HEIGHT_STYLE,
        },
        className: getCellClassName(
          INTERNAL_REFERENCE_CELL_CLASS_NAME,
          Boolean(selectedColumn?.length) && 'cell-with-references'
        ),
        data: {
          referenceType: ColumnReferenceType,
          sheetName: initialObject?.name || '',
        },
      };
      acc[key] = cell;
    }
    iterableRowConfig.forEach((row, rowIndex) => {
      const rowNumber = rowIndex + 1;
      const key = `${colLegend}${rowNumber}`;
      let value = '';
      let expr = '';
      let format = '';
      const persistedCell = initialObject?.content?.cells?.[key] as SpreadsheetCellWithStyle | undefined;
      const rowData = initialObject?.content?.row_data as
        | Record<string | number, { row_height?: number | null }>
        | undefined;
      const rowHeight = getRowDataByIndex(rowData, rowIndex, rowNumber)?.row_height;

      const fillStyle = getFillStyle(persistedCell?.fill);
      const borderStyle = getBorderStyle(persistedCell?.border);
      const rowHeightStyle = getXlsxRowHeightStyle(rowHeight);
      const rowReferenceHeightStyle = getSelectedRowsForReferenceRow(rowNumber).length
        ? ROW_REFERENCE_XLSX_ROW_HEIGHT_STYLE
        : {};

      const style = {
        ...fillStyle,
        ...borderStyle,
        ...widthStyle,
        ...rowHeightStyle,
        ...rowReferenceHeightStyle,
      };

      let font: Font = {};
      let alignment = null;
      let dataType = '';
      let rowSpan;
      let colSpan;
      if (mergedCells[key]) {
        rowSpan = mergedCells[key].row_span;
        colSpan = mergedCells[key].col_span;
      }
      if (persistedCell) {
        value = persistedCell.value ?? '';
        expr = persistedCell.expr ?? '';
        format = persistedCell.format ?? '';
        font = persistedCell.font || {};
        dataType = persistedCell.data_type || '';
        alignment = persistedCell.alignment ?? null;
      }
      const verticalAlignmentStyle = getVerticalAlignmentStyle(alignment);
      const wrapClassName = isPreview ? 'xlsx-wrap-text' : undefined;
      const readOnly = isPreview;
      const cell: SpreadsheetNoteConfigCell = {
        alias: '',
        columnLegend: colLegend,
        expr,
        key,
        colSpan,
        rowSpan,
        style: {
          ...style,
          ...verticalAlignmentStyle,
        },
        rowNumber,
        value,
        readOnly,
        //   we should probably combine these into one excel property
        excelProps: {
          format,
          font,
          dataType,
          alignment,
        },
        useValueIfInvalidExpr: true,
        valueViewer: ExcelFormattedValueViewer,
        ...(wrapClassName ? { className: wrapClassName } : {}),
        data: {
          sheetName: initialObject?.name || '',
        },
      };

      acc[key] = {
        ...cell,
      };
    });
    return acc;
  }, {});

  cells = {
    ...cells,
    ...columnCells,
  };

  if (isReferenceSheet) {
    const widthStyle = {
      minWidth: '175px',
    };
    iterableRowConfig.forEach((row, rowIndex) => {
      // rows - TODO validate it is on the right sheet
      const selectedRows = getSelectedRowsForReferenceRow(rowIndex);
      const rowNumber = rowIndex;
      const key = `${referenceColumnLegend}${rowNumber}`;
      if (rowIndex === 0) {
        return;
      }

      const baseOptions = attributeReferenceConfig?.rows || [];

      // Enrich options with ordinate metadata for AI suggestion chips
      // Pass the row index to get metadata specific to this row
      const options = baseOptions.map(option => {
        const ordinateMetadata = mapper?.getOrdinateMetadata(
          option.value as string,
          rowIndex.toString(),
          'row',
          tableData?.sheetName,
          tableData?.documentId
        );
        return {
          ...option,
          ordinate: ordinateMetadata,
        };
      });

      const cell: SpreadsheetNoteConfigCell = {
        key,
        rowNumber,
        columnLegend: referenceColumnLegend,
        valueViewer: (props: ValueViewerProps) =>
          isMultiCell ? (
            <MultiSelectValueViewer
              {...(props as MultiSelectSpreadsheetValueViewerProps)}
              options={options}
              useLabel={false}
              useAliasList={false}
            />
          ) : null,
        dataEditor: (props: DataEditorProps) =>
          isMultiCell ? (
            <SpreadsheetGridSelect
              {...props}
              alignMenuToCellTop
              extraStyles={spreadsheetNoteReferenceListStyles(zoomLevel ?? 1)}
              isMulti
              matchFrom="any"
              menuBoundaryMaxWidth={XLSX_PREVIEW_MENU_BOUNDARY_MAX_WIDTH}
              menuBoundarySelector={XLSX_PREVIEW_MENU_BOUNDARY_SELECTOR}
              menuPosition="fixed"
              options={options}
              usePortal
            />
          ) : null,
        readOnly: false,
        value: selectedRows,
        style: {
          ...widthStyle,
          ...(selectedRows?.length ? ROW_REFERENCE_XLSX_ROW_HEIGHT_STYLE : {}),
        },
        ...(selectedRows?.length
          ? { className: getCellClassName(ROW_REFERENCE_CELL_CLASS_NAME, 'cell-with-references') }
          : {}),
        data: {
          referenceType: RowReferenceType,
          sheetName: initialObject?.name || '',
        },
      };
      cells[key] = cell;
    });
  }
  return cells;
};

export default spreadsheetNoteParser;
